"use client";

import { useState, type ComponentType } from "react";
import { DirectPaths } from "@/config/routes";
import { cn } from "@/lib/utils";
import TrackedLink from "@/app/components/icm 3.0/shared/TrackedLink";
import {
    BoxesIcon,
    Building2Icon,
    ChartAreaIcon,
    ChartNoAxesCombinedOrangeIcon,
    ChartSplineIcon,
    CircleDollarSignIcon,
    CurrencyInrIcon,
    GlobeIcon,
    HandCoinsBlueIcon,
} from "@/app/assets/icons/investment-frontier/icons";

type MarketTab = "global" | "indian";

type Feature = {
    title: string;
    description: string;
    Icon: ComponentType;
};

type PanelConfig = {
    gradientClass: string;
    /** Hero logo tile border — Global midnight-blue/200, Indian sunrise-orange/200 */
    heroTileBorderClass: string;
    title: string;
    description: string;
    ctaLabel: string;
    href: string;
    analyticsEvent: string;
    cleverTapEvent: string;
    features: readonly Feature[];
    HeroIcon: ComponentType<{ size?: "md" | "lg" }>;
};

const TABS: { id: MarketTab; label: string }[] = [
    { id: "global", label: "Global Stocks" },
    { id: "indian", label: "Indian Stocks" },
];

/** Global Stocks — Figma web 2383:5114 / mobile logo 2541:30682 */
const GLOBAL_PANEL: PanelConfig = {
    gradientClass: "bg-[linear-gradient(166.67deg,#DEE8FB_30.53%,#FFFFFF_83.3%)]",
    heroTileBorderClass: "border-[#C5D8F8]",
    title: "Own the companies defining the global economy.",
    description:
        "Diversify beyond India with direct access to the largest, most innovative companies on the planet — priced in USD.",
    ctaLabel: "Explore Global Stocks",
    href: DirectPaths.GLOBAL + DirectPaths.US_STOCKS,
    analyticsEvent: "Home_Nav_Bar_US_Stocks",
    cleverTapEvent: "Navigation_Shares_US_Stocks",
    HeroIcon: GlobeIcon,
    features: [
        {
            title: "Own world leading companies",
            description: "Invest in companies shaping the future.",
            Icon: ChartSplineIcon,
        },
        {
            title: "Invest in iconic global brands",
            description: "Back globally recognized market leaders.",
            Icon: Building2Icon,
        },
        {
            title: "Diversify globally",
            description: "Expand your portfolio beyond India.",
            Icon: HandCoinsBlueIcon,
        },
        {
            title: "Gain USD Exposure",
            description: "Benefit from investing in the world's reserve currency.",
            Icon: CircleDollarSignIcon,
        },
    ],
};

/** Indian Stocks — Figma web 4099:47640 / mobile logo 7690:179484 */
const INDIAN_PANEL: PanelConfig = {
    gradientClass: "bg-[linear-gradient(167.23deg,#FCEAE0_24.53%,#FFFFFF_83.79%)]",
    heroTileBorderClass: "border-[#FBCBAD]",
    title: "Back one of the fastest-growing major economies.",
    description:
        "Invest in the NSE & BSE giants powering India's structural growth — in INR, with no overseas custody to manage.",
    ctaLabel: "Explore InCred Money Broking",
    href: DirectPaths.STOCKS,
    analyticsEvent: "Home_Explore_Indian_Stocks",
    cleverTapEvent: "Navigation_Broking",
    HeroIcon: ({ size = "md" }) => (
        <CurrencyInrIcon size={size === "lg" ? "lg" : "md"} />
    ),
    features: [
        {
            title: "Back India's growth story",
            description: "Invest in India's long-term economic growth.",
            Icon: ChartAreaIcon,
        },
        {
            title: "Own leading Indian companies",
            description: "Build wealth with India's market leaders.",
            Icon: BoxesIcon,
        },
        {
            title: "Future-defining sectors",
            description: "Invest across sectors shaping tomorrow.",
            Icon: ChartNoAxesCombinedOrangeIcon,
        },
        {
            title: "Trade in INR, familiar markets",
            description: "Seamlessly invest in Indian markets.",
            Icon: () => <CurrencyInrIcon size="sm" />,
        },
    ],
};

const PANELS: Record<MarketTab, PanelConfig> = {
    global: GLOBAL_PANEL,
    indian: INDIAN_PANEL,
};

/**
 * Investment Frontier — Figma desktop 2383:5106 / mobile 2541:30673
 * Indian Stocks panel — Figma 4099:47640
 */
const InvestmentFrontier = () => {
    const [activeTab, setActiveTab] = useState<MarketTab>("global");
    const panel = PANELS[activeTab];
    const { HeroIcon } = panel;

    return (
        <section
            aria-labelledby="investment-frontier-heading"
            className="w-full px-4 py-10 md:px-[120px] md:pt-[120px] md:pb-0"
        >
            <div className="mx-auto flex w-full max-w-[1200px] flex-col items-center gap-6 md:gap-12">
                <header className="flex w-full flex-col items-center gap-1 text-center md:gap-2">
                    <p className="w-full text-base font-semibold uppercase leading-[22px] tracking-[2px] text-brand-primary-500 md:text-xl md:leading-[26px]">
                        Your Investment Frontier
                    </p>
                    <h2
                        id="investment-frontier-heading"
                        className="w-full text-2xl font-bold leading-8 text-black md:text-5xl md:leading-[58px]"
                    >
                        <span className="md:hidden">
                            Local growth or
                            <br />
                            global dominance?
                        </span>
                        <span className="hidden md:inline">
                            Local growth or global dominance?
                        </span>
                    </h2>
                </header>

                <div className="flex w-full flex-col items-center gap-6 md:gap-5">
                    <div
                        role="tablist"
                        aria-label="Market type"
                        className="flex w-full items-center overflow-hidden rounded-[8px] bg-[#F5F5F5] p-2 md:w-[344px]"
                    >
                        {TABS.map((tab) => {
                            const isActive = activeTab === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    type="button"
                                    role="tab"
                                    aria-selected={isActive}
                                    id={`investment-frontier-tab-${tab.id}`}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={cn(
                                        "flex min-w-0 flex-1 items-center justify-center rounded-md px-3 py-2 text-base leading-[22px] transition-colors md:text-lg md:leading-6",
                                        isActive
                                            ? "bg-white font-medium text-brand-primary-500 shadow-[0_1px_3px_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)]"
                                            : "font-regular text-[#A5A5A5]",
                                    )}
                                >
                                    {tab.label}
                                </button>
                            );
                        })}
                    </div>

                    <div
                        role="tabpanel"
                        aria-labelledby={`investment-frontier-tab-${activeTab}`}
                        className={cn(
                            "flex w-full flex-col gap-6 overflow-hidden p-4 md:h-[567px] md:flex-row md:justify-between md:gap-[79px] rounded-[var(--radius-xl)] md:rounded-2xl md:border md:border-surface-stroke md:px-6 md:pb-6 md:pt-10",
                            panel.gradientClass,
                        )}
                    >
                        <div className="flex w-full flex-col gap-8 md:w-[396px] md:shrink-0 md:justify-between md:gap-0">
                            <div className="flex flex-col gap-8 md:gap-[47px]">
                                {/* Hero logo tile — mobile 62×66 @ 40px; desktop 82×85 @ 60px */}
                                <div
                                    className={cn(
                                        "flex h-[66px] w-[62px] items-center justify-center overflow-hidden rounded-2xl border border-solid bg-white md:h-[85px] md:w-[82px]",
                                        panel.heroTileBorderClass,
                                    )}
                                >
                                    <span className="md:hidden">
                                        <HeroIcon size="md" />
                                    </span>
                                    <span className="hidden md:inline-flex">
                                        <HeroIcon size="lg" />
                                    </span>
                                </div>

                                <div className="flex flex-col gap-3">
                                    <h3
                                        className={cn(
                                            "text-2xl font-bold leading-8 text-black md:text-4xl",
                                            activeTab === "indian"
                                                ? "md:leading-10"
                                                : "md:leading-[44px]",
                                        )}
                                    >
                                        {panel.title}
                                    </h3>
                                    <p className="max-w-[334px] text-base font-regular leading-[22px] text-[var(--colors-text-body-text-1,#767676)] md:max-w-none md:text-2xl md:leading-8">
                                        {panel.description}
                                    </p>
                                </div>
                            </div>

                            <TrackedLink
                                href={panel.href}
                                event={panel.analyticsEvent}
                                cleverTapEvent={panel.cleverTapEvent}
                                className="mt-auto hidden h-12 w-full items-center justify-center rounded-[var(--radius-m)] border border-cta-secondary-stroke bg-cta-secondary px-6 py-3 text-base font-medium leading-[22px] text-cta-secondary-text no-underline transition-colors hover:border-cta-secondary-stroke-hover hover:bg-cta-secondary-hover hover:text-cta-secondary-text-hover md:inline-flex"
                            >
                                {panel.ctaLabel}
                            </TrackedLink>
                        </div>

                        <ul className="flex w-full flex-col gap-8 md:w-[522px] md:shrink-0 md:gap-[60px] md:pt-[34px]">
                            {panel.features.map(({ title, description, Icon }) => (
                                <li key={title} className="flex items-start gap-5">
                                    <div className="flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white p-1">
                                        <Icon />
                                    </div>
                                    <div className="flex min-w-0 flex-col gap-1">
                                        <p className="text-base font-bold leading-[22px] text-black md:text-2xl md:font-semibold md:leading-8">
                                            {title}
                                        </p>
                                        <p className="text-base font-regular leading-[22px] text-[var(--colors-text-body-text-1,#767676)] md:text-lg md:leading-6">
                                            {description}
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ul>

                        <TrackedLink
                            href={panel.href}
                            event={panel.analyticsEvent}
                            cleverTapEvent={panel.cleverTapEvent}
                            className="inline-flex h-12 w-full items-center justify-center rounded-[var(--radius-m)] border border-cta-secondary-stroke bg-cta-secondary px-2 py-1 text-sm font-medium leading-5 text-cta-secondary-text no-underline transition-colors hover:border-cta-secondary-stroke-hover hover:bg-cta-secondary-hover hover:text-cta-secondary-text-hover md:hidden"
                        >
                            {panel.ctaLabel}
                        </TrackedLink>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default InvestmentFrontier;
